import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Server, Shield, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";

export default function LDAPConfigPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/admin/ldap-config`, { headers: getAuthHeaders() });
      setConfig(resp.data);
    } catch { toast.error("Failed to load LDAP config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/ldap-config`, config, { headers: getAuthHeaders() });
      toast.success("LDAP configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await axios.post(`${API}/admin/ldap-test`, {}, { headers: getAuthHeaders() });
      setTestResult({ success: true, message: resp.data.message });
      toast.success("LDAP connection successful");
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || "Connection failed" });
      toast.error("LDAP connection failed");
    } finally { setTesting(false); }
  };

  const update = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  if (loading || !config) {
    return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading LDAP configuration...</div></div></Layout>;
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="ldap-config-page">
        <PageHeader title="Active Directory / LDAP" subtitle="Configure directory authentication for your organization" testId="ldap-config-title">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleTest} disabled={testing || !config.enabled} className="rounded-none border-[#2563EB] text-[#2563EB]" data-testid="ldap-test-btn">
              {testing ? <><Loader2 size={14} className="mr-1 animate-spin" />Testing...</> : "Test Connection"}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="ldap-save-btn">
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </PageHeader>

        {testResult && (
          <div className={`border p-3 text-sm flex items-center gap-2 ${testResult.success ? "border-[#10B981] bg-[#F0FDF4] text-[#065F46]" : "border-[#DC2626] bg-[#FEF2F2] text-[#991B1B]"}`} data-testid="ldap-test-result">
            {testResult.success ? <Check size={16} /> : <X size={16} />}
            {testResult.message}
          </div>
        )}

        {/* Health Badge */}
        <IntegrationHealthBadge
          integration="ldap"
          onTestConnection={async () => {
            const resp = await axios.post(`${API}/admin/ldap-test`, {}, { headers: getAuthHeaders() });
            return { status: "success", message: resp.data.message };
          }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Connection Settings */}
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-4 flex items-center gap-2">
              <Server size={16} /> Connection Settings
            </h3>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium w-24">Enabled</Label>
                <button
                  onClick={() => update("enabled", !config.enabled)}
                  className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`}
                  data-testid="ldap-enabled-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${config.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
                <span className="text-xs text-[#64748B]">{config.enabled ? "Active" : "Disabled"}</span>
              </div>

              <div>
                <Label className="text-sm font-medium">Server Address</Label>
                <Input value={config.server || ""} onChange={(e) => update("server", e.target.value)} placeholder="ldap.company.com or 192.168.1.100" className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-server" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm font-medium">Port</Label>
                  <Input type="number" value={config.port || 389} onChange={(e) => update("port", parseInt(e.target.value) || 389)} className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-port" />
                </div>
                <div className="flex items-end">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={config.use_ssl || false} onChange={(e) => update("use_ssl", e.target.checked)} className="w-4 h-4" data-testid="ldap-ssl" />
                    <Label className="text-sm">Use SSL (LDAPS)</Label>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium">Base DN</Label>
                <Input value={config.base_dn || ""} onChange={(e) => update("base_dn", e.target.value)} placeholder="DC=company,DC=com" className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-base-dn" />
              </div>

              <div>
                <Label className="text-sm font-medium">Bind DN (Service Account)</Label>
                <Input value={config.bind_dn || ""} onChange={(e) => update("bind_dn", e.target.value)} placeholder="CN=svc_blueprint,OU=Service,DC=company,DC=com" className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-bind-dn" />
              </div>

              <div>
                <Label className="text-sm font-medium">Bind Password</Label>
                <Input type="password" value={config.bind_password || ""} onChange={(e) => update("bind_password", e.target.value)} placeholder="Service account password" className="mt-1 rounded-none text-sm" data-testid="ldap-bind-password" />
              </div>
            </div>
          </div>

          {/* Attribute Mapping */}
          <div className="bg-white border border-[#E2E8F0] p-6">
            <h3 className="font-bold text-sm text-[#0F172A] mb-4 flex items-center gap-2">
              <Shield size={16} /> Attribute Mapping
            </h3>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">User Search Filter</Label>
                <Input value={config.user_search_filter || ""} onChange={(e) => update("user_search_filter", e.target.value)} placeholder="(sAMAccountName={username})" className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-search-filter" />
                <p className="text-xs text-[#94A3B8] mt-1">Use {"{username}"} as placeholder</p>
              </div>

              <div>
                <Label className="text-sm font-medium">Employee Number Attribute</Label>
                <Input value={config.employee_number_attr || ""} onChange={(e) => update("employee_number_attr", e.target.value)} placeholder="employeeID" className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-emp-attr" />
              </div>

              <div>
                <Label className="text-sm font-medium">Display Name Attribute</Label>
                <Input value={config.name_attr || ""} onChange={(e) => update("name_attr", e.target.value)} placeholder="displayName" className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-name-attr" />
              </div>

              <div>
                <Label className="text-sm font-medium">Email Attribute</Label>
                <Input value={config.email_attr || ""} onChange={(e) => update("email_attr", e.target.value)} placeholder="mail" className="mt-1 rounded-none font-mono text-sm" data-testid="ldap-email-attr" />
              </div>

              <div>
                <Label className="text-sm font-medium">Default Role for New LDAP Users</Label>
                <Select value={config.default_role || "hourly"} onValueChange={(v) => update("default_role", v)}>
                  <SelectTrigger className="mt-1 rounded-none border-[#E2E8F0]" data-testid="ldap-default-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-none border border-[#E2E8F0]">
                    <SelectItem value="admin">Administrator/IT</SelectItem>
                    <SelectItem value="superintendent">Superintendent</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="hourly">Hourly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#F0F9FF] border border-[#BAE6FD] p-4">
          <h3 className="font-bold text-sm text-[#0369A1] mb-2">Setup Instructions</h3>
          <ol className="text-xs text-[#0C4A6E] space-y-1 list-decimal list-inside">
            <li>Enter your domain controller address and Base DN</li>
            <li>Create a service account in AD for this application with read-only access</li>
            <li>Enter the service account's Bind DN and password</li>
            <li>Verify the attribute names match your AD schema (most defaults work with Microsoft AD)</li>
            <li>Click "Test Connection" to verify connectivity</li>
            <li>Toggle "Enabled" to activate AD authentication</li>
            <li>Users will authenticate with their AD credentials. New users are auto-created with the default role.</li>
          </ol>
        </div>
      </div>
    </Layout>
  );
}
